import React, { useState } from "react";
import { Modal, Button, Popconfirm, message, notification } from "antd";
import { MailOutlined, PrinterOutlined, DownloadOutlined } from "@ant-design/icons";


const PDFViewerModal = ({ visible, onCancel, pdfUrl, handleEmail }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);

  const handleDownload = async () => {
    if (!pdfUrl) {
      notification.error({
        message: "Download Error",
        description: "PDF could not be found.",
      });
      return;
    }

    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      const  filename = pdfUrl.split("/").pop().split("?")[0];

      const contentType = response.headers.get("content-type") || "application/octet-stream";

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: contentType });

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `Estimate ${filename}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      notification.error({
        message: "Download Error",
        description: "Failed to download the file. Check console for details.",
      });
    }
  };

  const handlePrint = async () => {
    if (!pdfUrl) {
      message.error("PDF could not be found.");
      return;
    }

    setIsPrinting(true);

    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch PDF: ${response.status} ${response.statusText}`
        );
      }
      const pdfBlob = await response.blob();
      const blobUrl = URL.createObjectURL(pdfBlob);

      const printFrame = document.createElement("iframe");
      printFrame.style.position = "absolute";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "0";
      printFrame.src = blobUrl;

      document.body.appendChild(printFrame);

      printFrame.onload = () => {
        const printWindow = printFrame.contentWindow;
        let cleanedUp = false;

        const cleanup = () => {
          if (cleanedUp) return;
          cleanedUp = true;

          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(printFrame);
        };

        const mediaQueryList = printWindow.matchMedia("print");
        const handleChange = (mql) => {
          if (!mql.matches) {
            cleanup();
            mediaQueryList.removeEventListener("change", handleChange);
          }
        };
        mediaQueryList.addEventListener("change", handleChange);

        try {
          printWindow.focus();
          printWindow.print();
          // Stop the loading indicator as soon as the print dialog is triggered.
          setIsPrinting(false);
        } catch (error) {
          console.error("Failed to print PDF:", error);
          message.error("Could not open print dialog.");
          cleanup(); // Clean up if there's an error.
          setIsPrinting(false);
        }
      };
    } catch (error) {
      console.error("Error preparing PDF for print:", error);
      message.error({
        content:
          "Failed to load the PDF for printing. Please check console for details.",
        key: "printing",
        duration: 5,
      });
      setIsPrinting(false);
    }
  };

  // --- Custom Footer with Buttons ---
  const modalFooter = (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingTop: "16px",
      }}
    >
      <Button
        type="primary"
        onClick={handleDownload}
        icon={<DownloadOutlined />}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Download PDF
      </Button>
      {/* <Popconfirm
        title="Are you sure you want to email this to the client?"
        onConfirm={() => {
          setIsEmailing(true);
          handleEmail()
            .then(() => {
              message.success("Email sent successfully.");
            })
            .catch((error) => {
              console.error("Error sending email:", error);
              message.error("Failed to send the email.");
            })
            .finally(() => {
              setIsEmailing(false);
            });
        }}
        okText="Yes, Email"
        cancelText="No"
        placement="topRight"
      >
        <Button
          icon={<MailOutlined />}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          loading={isEmailing}
        >
          Email to Client
        </Button>
      </Popconfirm> */}
      <Button
        type="primary"
        icon={<PrinterOutlined />}
        onClick={handlePrint}
        loading={isPrinting}
        style={{
          marginLeft: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Print PDF
      </Button>
    </div>
  );

  // Appending parameters to the URL to control the PDF viewer's UI.
  const viewerUrl = pdfUrl ? `${pdfUrl}#toolbar=0&navpanes=0` : "";

  return (
    <Modal
      title="Document Viewer"
      open={visible}
      onCancel={onCancel}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{
        height: "75vh",
        padding: 0,
        overflow: "hidden",
        borderRadius: "8px",
      }}
      footer={modalFooter}
      destroyOnClose // Unmounts children when closed to reset state
      zIndex={2000}
    >
      {viewerUrl ? (
        <iframe
          src={viewerUrl}
          title="PDF Viewer"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9fafb",
          }}
        >
          <p style={{ color: "#6b7280" }}>No PDF to display.</p>
        </div>
      )}
    </Modal>
  );
};

export default PDFViewerModal;
